import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { combineLatest } from 'rxjs';
import { LandingPageSummary, SiteSummary, TagFilter } from '../../models/cms-models';
import { LandingPageImportPreview } from '../../models/landing-page-import-models';
import { LandingPageListComponent } from '../../components/landing-page-list/landing-page-list';
import { LandingPagesService } from '../../services/landing-pages-service';
import { LandingPageMarkdownImportService } from '../../services/landing-page-markdown-import.service';
import { SiteContextService } from '../../services/site-context.service';

@Component({
  selector: 'app-landing-pages-page',
  imports: [CommonModule, FormsModule, LandingPageListComponent],
  templateUrl: './landing-pages-page.html',
  styleUrl: './landing-pages-page.css',
})
export class LandingPagesPage implements OnInit {
  private siteContext = inject(SiteContextService);
  private landingPagesService = inject(LandingPagesService);
  private landingPageMarkdownImportService = inject(LandingPageMarkdownImportService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  sites: SiteSummary[] = [];
  landingPages: LandingPageSummary[] = [];
  selectedSiteId: string | null = null;
  loading = false;
  error: string | null = null;
  isImportDialogOpen = false;
  importDragging = false;
  importSourceName: string | null = null;
  markdownImportContent = '';
  importPreview: LandingPageImportPreview | null = null;
  importAsDraft = false;
  importSaving = false;
  importError: string | null = null;
  tagFilter: TagFilter = {
    tags: [],
    tagMode: 'all',
  };
  private loadedSiteId: string | null = null;

  async ngOnInit(): Promise<void> {
    await this.siteContext.ensureLoaded();
    combineLatest([this.siteContext.sites$, this.siteContext.activeSiteId$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([sites, activeSiteId]) => {
        void this.syncSelectedSite(sites, activeSiteId);
      });
  }

  get selectedSiteName(): string {
    return this.sites.find((site) => site.id === this.selectedSiteId)?.name ?? 'site';
  }

  createLandingPage(): void {
    if (!this.selectedSiteId) {
      return;
    }

    void this.router.navigate(['/landing-pages/new']);
  }

  openImportDialog(): void {
    if (!this.selectedSiteId) {
      return;
    }

    this.resetImportState();
    this.isImportDialogOpen = true;
  }

  closeImportDialog(): void {
    if (this.importSaving) {
      return;
    }

    this.isImportDialogOpen = false;
    this.resetImportState();
  }

  openLandingPage(landingPageId: string): void {
    void this.router.navigate(['/landing-pages', landingPageId]);
  }

  openSitesPage(): void {
    void this.router.navigate(['/sites']);
  }

  async toggleTagFilter(tag: string): Promise<void> {
    const tags = this.tagFilter.tags.includes(tag)
      ? this.tagFilter.tags.filter((existingTag) => existingTag !== tag)
      : [...this.tagFilter.tags, tag];

    this.tagFilter = {
      tags,
      tagMode: 'all',
    };

    await this.reloadSelectedSite();
  }

  async clearFilters(): Promise<void> {
    this.tagFilter = {
      tags: [],
      tagMode: 'all',
    };

    await this.reloadSelectedSite();
  }

  async onImportFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    input.value = '';

    if (!file) {
      return;
    }

    await this.loadImportFile(file);
  }

  onImportDragOver(event: DragEvent): void {
    event.preventDefault();
    this.importDragging = true;
  }

  onImportDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.importDragging = false;
  }

  async onImportDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    this.importDragging = false;

    const files = event.dataTransfer?.files;
    if (!files?.length) {
      return;
    }

    if (files.length > 1) {
      this.importError = 'Drop a single Markdown file at a time.';
      return;
    }

    await this.loadImportFile(files[0]);
  }

  parseMarkdownImport(): void {
    if (!this.selectedSiteId || !this.markdownImportContent.trim()) {
      return;
    }

    try {
      this.importPreview = this.landingPageMarkdownImportService.parse(
        this.selectedSiteId,
        this.markdownImportContent,
        this.importSourceName,
      );
      this.importAsDraft = false;
      this.importError = null;
    } catch {
      this.importPreview = null;
      this.importError = 'Failed to parse the Markdown source.';
    }
  }

  returnToImportInput(): void {
    this.importPreview = null;
    this.importAsDraft = false;
    this.importError = null;
  }

  resetImportSource(): void {
    this.resetImportState();
  }

  async acceptMarkdownImport(): Promise<void> {
    if (!this.importPreview) {
      return;
    }

    this.importSaving = true;
    this.importError = null;

    const landingPage = this.importAsDraft
      ? {
          ...this.importPreview.landingPage,
          publicationState: 'Draft' as const,
          scheduledPublishAt: null,
          publishedAt: null,
        }
      : { ...this.importPreview.landingPage };

    try {
      const created = await this.landingPagesService.createLandingPage(landingPage);
      this.siteContext.setActiveSite(created.siteId);
      this.isImportDialogOpen = false;
      this.resetImportState();
      await this.router.navigate(['/landing-pages', created.id]);
    } catch {
      this.importError = 'Failed to create the imported landing page.';
    } finally {
      this.importSaving = false;
    }
  }

  summarySourceLabel(source: LandingPageImportPreview['summarySource']): string {
    switch (source) {
      case 'frontmatter-summary':
        return 'Frontmatter summary';
      case 'frontmatter-description':
        return 'Frontmatter description';
      case 'excerpt':
        return 'Excerpt before <!--more-->';
      case 'first-text-block':
        return 'First text block';
      default:
        return 'Default import summary';
    }
  }

  effectiveImportState(): LandingPageImportPreview['detectedPublicationState'] {
    if (!this.importPreview) {
      return 'Draft';
    }

    return this.importAsDraft ? 'Draft' : this.importPreview.detectedPublicationState;
  }

  private async syncSelectedSite(sites: SiteSummary[], activeSiteId: string | null): Promise<void> {
    this.sites = sites;

    if (!sites.length) {
      this.selectedSiteId = null;
      this.landingPages = [];
      this.loadedSiteId = null;
      return;
    }

    const targetSiteId = this.siteContext.ensureValidActiveSite(activeSiteId);
    if (!targetSiteId) {
      return;
    }

    this.selectedSiteId = targetSiteId;

    if (this.loadedSiteId === targetSiteId) {
      return;
    }

    this.landingPages = [];
    await this.loadLandingPages(targetSiteId);
  }

  private async loadLandingPages(siteId: string): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      this.landingPages = await this.landingPagesService.getSiteLandingPages(siteId, this.tagFilter);
      this.loadedSiteId = siteId;
    } catch {
      this.error = 'Failed to load landing pages for the selected site.';
    } finally {
      this.loading = false;
    }
  }

  private async reloadSelectedSite(): Promise<void> {
    if (!this.selectedSiteId) {
      return;
    }

    await this.loadLandingPages(this.selectedSiteId);
  }

  private async loadImportFile(file: File): Promise<void> {
    this.importSourceName = file.name;
    this.markdownImportContent = await file.text();
    this.importPreview = null;
    this.importAsDraft = false;
    this.importError = null;
  }

  private resetImportState(): void {
    this.importDragging = false;
    this.importSourceName = null;
    this.markdownImportContent = '';
    this.importPreview = null;
    this.importAsDraft = false;
    this.importSaving = false;
    this.importError = null;
  }
}
